import { useCallback, useEffect, useRef, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";

import { RefreshPageButton } from "components/RefreshPageButton";
import { ShipmentListContainer } from "components/Shipper/components/ShipmentListContainer";
import { ShipmentTemplateAndCreateShipmentFormDashboardItem } from "components/Shipper/components/ShipmentTemplateAndCreateShipmentFormDashboardItem";
import { SupplierShipmentListContainer } from "components/Shipper/components/SupplierShipments/SupplierShipmentListContainer";
import { useShipments } from "hooks";
import { useShipmentFiltersContext } from "hooks/useShipmentFiltersContext";
import { useSupplierShipments } from "hooks/useSupplierShipments";
import { DashboardItemLayout, PageLayout } from "layouts";
import { SHIPMENTS_PAGE_SIZE } from "utils";

import type { ShipmentListContainerRef } from "components/Shipper/components/ShipmentListContainer";
import type { SupplierShipmentListContainerRef } from "components/Shipper/components/SupplierShipments/SupplierShipmentListContainer";

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`horizontal-shipment-tabpanel-${index}`}
      aria-labelledby={`horizontal-shipment-tab-${index}`}
    >
      {value === index && children}
    </div>
  );
}

export function HorizontalDashboard() {
  const [tabValue, setTabValue] = useState<number>(0);
  const [myShipmentsPage, setMyShipmentsPage] = useState<number>(1);
  const [supplierShipmentsPage, setSupplierShipmentsPage] = useState<number>(1);
  const [leftExpanded, setLeftExpanded] = useState(true);
  const shipmentListRef = useRef<ShipmentListContainerRef>(null);
  const supplierShipmentListRef =
    useRef<SupplierShipmentListContainerRef>(null);

  const {
    shipments,
    error: shipmentsError,
    isLoading: shipmentsLoading,
    count: shipmentsCount,
    refreshShipments,
  } = useShipments({ page: myShipmentsPage, pageSize: SHIPMENTS_PAGE_SIZE });

  const {
    supplierShipments,
    error: supplierShipmentsError,
    isLoading: supplierShipmentsLoading,
    count: supplierShipmentsCount,
    refreshSupplierShipments,
  } = useSupplierShipments({
    page: supplierShipmentsPage,
    pageSize: SHIPMENTS_PAGE_SIZE,
  });

  const {
    registerRefreshCallback: registerRegularCallback,
    unregisterRefreshCallback: unregisterRegularCallback,
    filters: regularFilters,
  } = useShipmentFiltersContext("regular");

  const {
    registerRefreshCallback: registerSupplierCallback,
    unregisterRefreshCallback: unregisterSupplierCallback,
    filters: supplierFilters,
  } = useShipmentFiltersContext("supplier");

  useEffect(() => {
    registerRegularCallback(refreshShipments);
    return () => unregisterRegularCallback();
  }, [registerRegularCallback, unregisterRegularCallback, refreshShipments]);

  useEffect(() => {
    registerSupplierCallback(refreshSupplierShipments);
    return () => unregisterSupplierCallback();
  }, [
    registerSupplierCallback,
    unregisterSupplierCallback,
    refreshSupplierShipments,
  ]);

  useEffect(() => {
    setMyShipmentsPage(1);
  }, [regularFilters]);

  useEffect(() => {
    setSupplierShipmentsPage(1);
  }, [supplierFilters]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const refreshCurrentTab = () => {
    if (tabValue === 0) {
      refreshShipments();
    } else {
      refreshSupplierShipments();
    }
  };

  const handleRestoreLeftPanel = useCallback(() => {
    shipmentListRef.current?.closeFilter();
    supplierShipmentListRef.current?.closeFilter();
    setLeftExpanded(true);
  }, []);

  const handleRestoreLeftPanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        handleRestoreLeftPanel();
      }
    },
    [handleRestoreLeftPanel],
  );

  return (
    <PageLayout>
      <Grid container spacing={2}>
        <Grid
          item
          xs={leftExpanded ? 7 : 5}
          id="left-side-horizontal-dashboard"
          sx={{
            transition: "all 0.3s ease",
            cursor: !leftExpanded ? "pointer" : undefined,
          }}
          onClick={!leftExpanded ? handleRestoreLeftPanel : undefined}
          onKeyDown={!leftExpanded ? handleRestoreLeftPanelKeyDown : undefined}
          tabIndex={!leftExpanded ? 0 : undefined}
        >
          <DashboardItemLayout
            sx={{
              p: "2rem",
            }}
          >
            <ShipmentTemplateAndCreateShipmentFormDashboardItem
              refreshShipments={refreshCurrentTab}
            />
          </DashboardItemLayout>
        </Grid>
        <Grid
          item
          xs={leftExpanded ? 5 : 7}
          id="right-side-horizontal-dashboard"
          sx={{ transition: "all 0.3s ease" }}
        >
          <DashboardItemLayout
            sx={{
              p: "1rem",
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" justifyContent="start">
                {leftExpanded ? (
                  <Tooltip title="Click to expand section">
                    <IconButton
                      onClick={() => {
                        setLeftExpanded(false);
                      }}
                      size="small"
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box
                      sx={{
                        width: "3px",
                        height: "24px",
                        borderRadius: "2px",
                        backgroundColor: "primary.main",
                      }}
                    />
                    <Tooltip title="Click to restore section">
                      <IconButton
                        onClick={handleRestoreLeftPanel}
                        size="small"
                        color="primary"
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="horizontal shipment tabs"
                >
                  <Tab
                    label={`My Shipments (${shipmentsCount})`}
                    id="shipment-tab-0"
                    aria-controls="shipment-tabpanel-0"
                  />
                  <Tab
                    label={`Supplier Shipments (${supplierShipmentsCount})`}
                    id="shipment-tab-1"
                    aria-controls="shipment-tabpanel-1"
                  />
                </Tabs>
              </Stack>
              <RefreshPageButton
                id="dashboard-shipments-refresh"
                onRefresh={refreshCurrentTab}
                iconButton
              />
            </Box>
            <TabPanel value={tabValue} index={0}>
              <ShipmentListContainer
                ref={shipmentListRef}
                page={myShipmentsPage}
                setPage={setMyShipmentsPage}
                shipments={shipments}
                shipmentsError={shipmentsError}
                shipmentsLoading={shipmentsLoading}
                shipmentCount={shipmentsCount}
                onFilterExpanded={() => setLeftExpanded(false)}
                onFilterCollapsed={() => setLeftExpanded(true)}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <SupplierShipmentListContainer
                ref={supplierShipmentListRef}
                page={supplierShipmentsPage}
                setPage={setSupplierShipmentsPage}
                supplierShipments={supplierShipments}
                supplierShipmentsError={supplierShipmentsError}
                supplierShipmentsLoading={supplierShipmentsLoading}
                supplierShipmentCount={supplierShipmentsCount}
                onFilterExpanded={() => setLeftExpanded(false)}
                onFilterCollapsed={() => setLeftExpanded(true)}
              />
            </TabPanel>
          </DashboardItemLayout>
        </Grid>
      </Grid>
    </PageLayout>
  );
}
